import React, { useContext, useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import { DialogResponse, DialogResponseCode, DialogType, modeUIContext, setIsEditingForm, showDialog, showSuccessNotification }
    from '@moderepo/mode-ui-state';
import { capitalizeAll } from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent, selectProjectById, updateProjectSettings } from '@moderepo/mode-data-state-ritz';
import { ProjectUISettingsInfo, ProjectUISettingsInfoProps } from '../../components/admin';
import { useRitzTranslation } from '../../utils/customHooks';
import { isProjectCustomUIConfig } from '../../utils/ritzUIExtensionConfigValidation';
import { SettingsErrorLogger } from '../../utils/logger';


const useStyle = makeStyles(() => {
    return {
        root: {
            width : '100%',
            height: '100%',
        },
    };
});


interface ProjectUISettingsContainerProps {
    readonly projectId: number;
}


/**
 * This is the wrapper for the ProjectUISettingsInfo component. It provide the logics for fetching and updating the project's UI settings
 */
export const ProjectUISettingsContainer: React.FC<ProjectUISettingsContainerProps> = (props: ProjectUISettingsContainerProps) => {
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, projectId);
    const loggedInAgent = selectLoggedInAgent(dataState);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();


    const [isEditing, setIsEditing] = useState<boolean>();
    const [inputError, setInputError] = useState<string | undefined>();



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputError(undefined);

        // also change global edit mode
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    const onCancelEdit = useCallback(() => {
        closeEditing();
    }, [closeEditing]);



    const onFormSubmit = useCallback(async (inputs: {
        readonly uiSettings: string | undefined
    }) => {

        if (!loggedInAgent || !project) {
            return;
        }

        try {

            // if inputs.uiSettings is not an empty string, try to parse it into a Object
            const uiSettingsObj = inputs.uiSettings ? JSON.parse(inputs.uiSettings) : undefined;

            // uiSettingsObj is OPTIONAL. If it is undefined, it means the user cleared the settings and we need to save it as an empty string
            // uiSettingsObj is NOT undefined that mean the user provided some settings therefore we need to validate it using the
            // 'isProjectCustomUIConfig' type guard function
            const errorLogger = new SettingsErrorLogger('"Project UI Settings"', uiSettingsObj ?? {});
            if (!uiSettingsObj || isProjectCustomUIConfig(uiSettingsObj, errorLogger, [])) {

                // Only make API call to update the uiSettings if the new uiSettingsObj is different from the current uiSettings
                // The 2 uiSettings objects are different if their STRINGIFIED values are different.
                if (
                    (uiSettingsObj === undefined && project.supportToolSettings.uiSettings !== undefined)       // user cleared the settings
                    || (uiSettingsObj !== undefined && project.supportToolSettings.uiSettings === undefined)    // user added settings
                    || (uiSettingsObj && project.supportToolSettings.uiSettings                                 // settings changed
                        && JSON.stringify(project.supportToolSettings.uiSettings) !== JSON.stringify(uiSettingsObj)
                    )
                ) {

                    // UI Settings is validated, we can now make API call to update the project settings. HOWEVER, we need to confirm if the
                    // user want to continue before making the API call to update the settings since this can change how the project UI look
                    // Confirm before removing home
                    uiDispatch(showDialog({
                        type          : DialogType.CONFIRM,
                        title         : 'Update project UI Settings',
                        message       : 'Are you sure you want to update this project\'s UI Settings? This will change how the project look.',
                        positiveButton: 'label.continue',
                        negativeButton: 'label.cancel',
                        onClose       : async (response?: DialogResponse): Promise<void> => {

                            if (response && response.code === DialogResponseCode.POSITIVE) {
                                await dataDispatch(updateProjectSettings(project.id, loggedInAgent.id, {
                                    uiSettings: uiSettingsObj ? JSON.stringify(uiSettingsObj, undefined, 4) : '',
                                }));

                                uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.project' }));
                                closeEditing();
                            }
                        },
                    }));
                } else {
                    uiDispatch(showSuccessNotification('Nothing changed so don\'t need to make API call'));
                    closeEditing();
                }
            } else {
                setInputError('Invalid UI Settings. See the browser\' console for more info.');
            }
        } catch (error) {
            setInputError(`Invalid UI Settings: ${(error as Error).message}`);
        }
    }, [closeEditing, dataDispatch, loggedInAgent, project, uiDispatch]);



    /**
     * Validate the value string and make sure it is a valid JSON, reformat the string and return the formatted string using 4 spaces tab.
     * Throw an error if the JSON is invalid.
     */
    const onValidateJSON = useCallback((value: string): string => {
        return JSON.stringify(JSON.parse(value), undefined, 4);
    }, []);



    const fieldsSettings = useMemo((): ProjectUISettingsInfoProps['fields'] => {
        return {
            uiSettings: {
                label   : 'UI Settings',
                editable: true,
                value   : project ? JSON.stringify(project.supportToolSettings.uiSettings, undefined, 4) : undefined,
                error   : inputError || '',
            },
        };
    }, [inputError, project]);



    return (
        <ProjectUISettingsInfo
            className={clsx(classes.root, isEditing && 'editing')}
            title="Project UI Settings"
            icon="settings"
            fields={fieldsSettings}
            isEditing={isEditing}
            actions={{
                formatJson: {
                    label  : capitalizeAll(trans('label.format_json')),
                    onClick: onValidateJSON,
                },
                startEdit: {
                    onClick: () => {
                        onChangeEditingMode(true);
                    },
                },
                cancelEdit: {
                    label  : capitalizeAll(trans('label.cancel')),
                    onClick: onCancelEdit,
                },
                saveUpdate: {
                    label  : capitalizeAll(trans('label.save')),
                    onClick: onFormSubmit,
                },
            }}
            onFieldClicked={() => {
                onChangeEditingMode(true);
            }}
            onFormInputChange={() => {
                setInputError(undefined);
            }}
        />
    );
};
